import React from "react";
import { formatCurrency } from "../lib/api";
import { useCompany } from "../context/CompanyContext";

const InvoicePrintCard = ({ invoice, verificationUrl, company: companyProp }) => {
  const { company: liveCompany } = useCompany();
  const company = companyProp || liveCompany;
  if (!invoice) return null;
  const hasHours = invoice.line_items?.some((l) => l.hours);
  const hasRate = invoice.line_items?.some((l) => l.rate);

  return (
    <article
      className="invoice-print-area bg-white text-[#222] rounded-lg shadow-2xl px-10 md:px-14 py-12"
      style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
    >
      {/* Header */}
      <header className="border-b-2 border-[#222] pb-5 mb-8">
        <h1 className="text-[32px] font-bold text-[#222] mb-4">INVOICE</h1>
        <div className="grid md:grid-cols-2 gap-10 text-[13px] text-[#555] leading-[1.6]">
          <div>
            <div className="text-[12px] font-bold text-[#222] mb-1">From:</div>
            <div className="text-[14px] text-[#222]">{invoice.from_party?.name}</div>
            <div className="text-[12px] text-[#777] mt-0.5">{invoice.from_party?.company}</div>
            <div className="text-[12px] text-[#777]">{invoice.from_party?.email}</div>
            <div className="text-[11px] text-[#999] mt-2">Invoice #{invoice.invoice_number}</div>
          </div>
          <div>
            <div className="text-[12px] font-bold text-[#222] mb-1">To:</div>
            <div className="text-[14px] text-[#222]">{invoice.to_party?.company}</div>
            {invoice.to_party?.address && <div className="text-[12px] text-[#777]">{invoice.to_party.address}</div>}
            {invoice.to_party?.line2 && <div className="text-[12px] text-[#777]">{invoice.to_party.line2}</div>}
            {invoice.to_party?.country && <div className="text-[12px] text-[#777]">{invoice.to_party.country}</div>}
          </div>
        </div>
      </header>

      {/* Details */}
      <section className="grid md:grid-cols-2 gap-10 mb-7 text-[13px] text-[#333]">
        <div>
          <div className="text-[11px] font-bold uppercase text-[#777] mb-1.5">Invoice Date</div>
          <div>{invoice.invoice_date}</div>
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase text-[#777] mb-1.5">
            {invoice.period ? "Invoice Period" : "Project"}
          </div>
          <div>{invoice.period || invoice.project_name}</div>
        </div>
      </section>

      {invoice.scope_brief_text && (
        <div className="mb-7 px-4 py-3 bg-[#fafafa] border border-[#eee] text-[12px] text-[#444] leading-[1.65]">
          <div className="text-[10px] font-bold uppercase text-[#777] mb-1">Scope Brief · AYI_all (Absolute Zero)</div>
          {invoice.scope_brief_text}
        </div>
      )}

      {invoice.project_stats && (
        <div className="mb-6 px-3 py-2.5 bg-[#fafafa] border border-[#eee] text-[10.5px] text-[#999]">
          <span className="font-semibold text-[#555]">Project Stats: </span>
          {invoice.project_stats}
        </div>
      )}

      {/* Line items */}
      <table className="w-full border-collapse mb-7">
        <thead>
          <tr className="bg-[#f8f8f8] border-t-2 border-b-2 border-[#222]">
            <th className="text-left text-[12px] font-bold text-[#222] uppercase py-3 px-2">
              Description of Services
            </th>
            {hasHours && <th className="text-center text-[12px] font-bold text-[#222] uppercase py-3 px-2 w-[60px]">Hrs</th>}
            {hasRate && <th className="text-right text-[12px] font-bold text-[#222] uppercase py-3 px-2 w-[80px]">Rate</th>}
            <th className="text-right text-[12px] font-bold text-[#222] uppercase py-3 px-2 w-[90px]">Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.line_items?.map((item, i) => (
            <tr key={i} className="border-b border-[#eee] last:border-b-2 last:border-[#222]">
              <td className="py-2.5 px-2 align-top">
                <strong className="text-[12px] text-[#333] block">{item.description}</strong>
                {item.detail && <div className="text-[11px] text-[#666] leading-[1.45] mt-1">{item.detail}</div>}
              </td>
              {hasHours && (
                <td className="text-center text-[12px] text-[#333] py-2.5 px-2 align-top">{item.hours ?? "—"}</td>
              )}
              {hasRate && (
                <td className="text-right text-[12px] text-[#333] py-2.5 px-2 align-top">
                  {item.rate ? `$${Number(item.rate).toFixed(2)}` : "—"}
                </td>
              )}
              <td className="text-right text-[12px] font-bold text-[#222] py-2.5 px-2 align-top">
                {formatCurrency(item.amount, invoice.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <section className="ml-auto w-full md:w-[380px] mb-7">
        <div className="flex justify-between py-2.5 text-[12px] text-[#333] border-b border-[#eee]">
          <span>Subtotal</span>
          <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
        </div>
        {invoice.adjustments?.map((adj, i) => (
          <div key={i} className="flex justify-between py-2.5 text-[12px] text-[#333] border-b border-[#eee]">
            <span className="flex items-center gap-2">
              {adj.label}
              {adj.is_credit && (
                <span className="bg-[#22c55e] text-white px-1.5 py-0.5 text-[9px] font-bold rounded">CREDIT</span>
              )}
            </span>
            <span>{adj.is_credit ? "-" : ""}{formatCurrency(adj.amount, invoice.currency)}</span>
          </div>
        ))}
        <div className="flex justify-between py-3 mt-2 text-[15px] font-bold text-[#222] border-t-2 border-b-2 border-[#222]">
          <span>Total Amount Due</span>
          <span>{formatCurrency(invoice.total, invoice.currency)} <span className="text-[10px] text-[#999] font-normal ml-1">{invoice.currency}</span></span>
        </div>
      </section>

      {/* Terms / Notes */}
      <section className="bg-[#f8f8f8] border-l-[3px] border-[#d4af37] px-5 py-4 mb-6 text-[12px] text-[#333] leading-[1.6]">
        <h4 className="text-[12px] font-bold text-[#222] mb-2">Terms & Conditions</h4>
        <p><strong>Payment Method:</strong> {invoice.payment_method || company.payment_instructions}</p>
        {invoice.due_date && <p className="mt-1.5"><strong>Due Date:</strong> {invoice.due_date}</p>}
        {invoice.notes && <p className="mt-2.5"><strong>Note:</strong> {invoice.notes}</p>}
        {invoice.project_summary && <p className="mt-2.5"><strong>Project Summary:</strong> {invoice.project_summary}</p>}
        {invoice.contact_email && <p className="mt-2"><strong>Contact:</strong> {invoice.contact_email}</p>}
      </section>

      {/* Sources & verification */}
      {(invoice.rate_sources?.length > 0 || verificationUrl) && (
        <section className="border-t border-[#eee] pt-5 mb-6 text-[10.5px] text-[#888]">
          {invoice.rate_sources?.length > 0 && (
            <>
              <div className="font-bold text-[#555] uppercase tracking-wide mb-1.5">Sources & Methodology</div>
              <ul className="space-y-0.5 mb-3">
                {invoice.rate_sources.slice(0, 4).map((s, i) => (
                  <li key={i}>
                    {s.publisher || "Source"}{s.url ? ` — ${s.url}` : ""}
                    {s.field ? ` · ${s.field}` : ""}
                  </li>
                ))}
              </ul>
              {invoice.rate_snapshot_id && (
                <div className="text-[#aaa]">Rate snapshot: <span className="font-mono">{invoice.rate_snapshot_id}</span></div>
              )}
            </>
          )}
          {verificationUrl && (
            <div className="mt-3">
              <strong>Verify:</strong> <a className="underline" href={verificationUrl}>{verificationUrl}</a>
            </div>
          )}
        </section>
      )}

      <footer className="border-t border-[#eee] pt-3 text-[11px] text-[#888] text-center">
        <p>Thank you for your business!</p>
        <p className="mt-1.5">© 2026 {invoice.from_party?.company} | {invoice.from_party?.name} | {invoice.from_party?.email}</p>
        <p className="mt-1 text-[#bbb] font-mono uppercase tracking-[0.18em] text-[9px]">{company.brand_footer}</p>
      </footer>
    </article>
  );
};

export default InvoicePrintCard;
